package org.mkui.component

import com.macrofocus.common.interval.MutableBoundedInterval
import com.macrofocus.common.properties.MutableProperty
import com.macrofocus.common.properties.SimpleProperty
import com.macrofocus.common.selection.MutableSelection
import com.macrofocus.common.selection.MutableSingleSelection
import com.macrofocus.common.selection.SimpleSingleSelection
import org.mkui.canvas.CPCanvas
import org.mkui.color.MkColor
import org.mkui.component.button.*
import org.mkui.component.input.CPColorPicker
import org.mkui.component.input.CPFontPicker
import org.mkui.component.input.CPSlider
import org.mkui.component.input.CPSpinner
import org.mkui.component.label.CPLabel
import org.mkui.component.list.CPCheckBoxList
import org.mkui.component.panel.*
import org.mkui.font.MkFont

interface CPFactory : ComponentFactory {
    fun createLabel(s: String?): CPLabel
    fun <E> createComboBox(algorithmSelection: MutableSingleSelection<E> = SimpleSingleSelection(), algorithms: Iterable<E> = emptyList()): CPComboBox<E>

    fun <E> createCheckBoxList(selection : MutableSingleSelection<E>, checks: MutableSelection<E>, values: Iterable<E>): CPComponent
    fun createVerticalPanel(): CPComponent
    fun createFormPanel(): CPFormPanel
    fun createSplitMenuButton(): CPSplitMenuButton
    fun createIntegerSpinner(): CPSpinner<Int?>
    fun createIntegerSpinner(property: MutableProperty<Int?>): CPSpinner<Int?>
    fun createDoubleSpinner(): CPSpinner<Double>
    fun createDoubleSpinner(property: MutableProperty<Double?>): CPSpinner<Double>
    fun createDoubleSpinner(interval: MutableBoundedInterval): CPSpinner<Double>

    fun createColorPicker(): CPColorPicker
    fun createColorPicker(property: MutableProperty<MkColor?>): CPColorPicker
    fun createFontPicker(property: MutableProperty<MkFont?>): CPFontPicker
    abstract fun createCanvas(): CPCanvas
    fun createOverlayPanel(): CPOverlayPanel
    fun createSlider(): CPSlider
    fun createSlider(interval: MutableBoundedInterval): CPSlider
    fun createCheckBox(s: String, nothing: Nothing?): CPCheckBox
    fun createVerticalFlowPanel(vararg components: CPComponent): CPVerticalFlowPanel
    fun <T> createRadioButtons(values: Array<T>): CPRadioButtons<T>
    fun <T> createRadioButtons(selection: MutableSingleSelection<T>, values: Array<T>): CPRadioButtons<T>

    fun createButton(s: String): CPButton
    fun createFillMiddlePanel(): CPFillMiddlePanel
    fun createVerticalFillMiddlePanel(): CPVerticalFillMiddlePanel
    fun createGridPanel(rows: Int, columns: Int, vararg components: CPComponent): CPComponent
    fun createSwitch(): CPSwitch
    fun createSwitch(property: MutableProperty<Boolean>?): CPSwitch

    companion object {
        val instance = object : CPFactory {
            override fun createFillMiddlePanel(): CPFillMiddlePanel {
                return CPFillMiddlePanel()
            }

            override fun createVerticalFillMiddlePanel(): CPVerticalFillMiddlePanel {
                return CPVerticalFillMiddlePanel()
            }

            override fun createButton(s: String): CPButton {
                return CPButton(text = SimpleProperty.newInstance(s))
            }

            override fun <T> createRadioButtons(values: Array<T>): CPRadioButtons<T> {
                return CPRadioButtons<T>(values = values)
            }

            override fun <T> createRadioButtons(selection: MutableSingleSelection<T>, values: Array<T>): CPRadioButtons<T> {
                return CPRadioButtons<T>(selection, values)
            }

            override fun createVerticalFlowPanel(vararg components: CPComponent): CPVerticalFlowPanel {
                return CPVerticalFlowPanel(*components)
            }

            override fun createCheckBox(s: String, nothing: Nothing?): CPCheckBox {
                return CPCheckBox(s, nothing)
            }

            override fun createSplitMenuButton(): CPSplitMenuButton {
                return CPSplitMenuButton()
            }

            override fun createIntegerSpinner(): CPSpinner<Int?> {
                return CPSpinner<Int?>()
            }

            override fun createIntegerSpinner(property: MutableProperty<Int?>): CPSpinner<Int?> {
                return CPSpinner<Int?>(property)
            }

            override fun createDoubleSpinner(): CPSpinner<Double> {
                return CPSpinner<Double>()
            }

            override fun createDoubleSpinner(property: MutableProperty<Double?>): CPSpinner<Double> {
                return CPSpinner<Double>(property)
            }

            override fun createDoubleSpinner(interval: MutableBoundedInterval): CPSpinner<Double> {
                return CPSpinner<Double>(interval)
            }

            override fun createColorPicker(): CPColorPicker {
                return CPColorPicker()
            }

            override fun createColorPicker(property: MutableProperty<MkColor?>): CPColorPicker {
                return CPColorPicker(property)
            }

            override fun createFontPicker(property: MutableProperty<MkFont?>): CPFontPicker {
                return CPFontPicker(property)
            }

            override fun createCanvas(): CPCanvas {
                return CPCanvas()
            }

            override fun createOverlayPanel(): CPOverlayPanel {
                return CPOverlayPanel()
            }

            override fun createSlider(): CPSlider {
                return CPSlider()
            }

            override fun createSlider(interval: MutableBoundedInterval): CPSlider {
                return CPSlider(interval)
            }

            override fun add(component: CPComponent) {
            }

            override fun createLabel(s: String?): CPLabel {
                return CPLabel(SimpleProperty.newInstance(s!!))
            }

            override fun <E> createComboBox(
                algorithmSelection: MutableSingleSelection<E>,
                algorithms: Iterable<E>
            ): CPComboBox<E> {
                return CPComboBox<E>(algorithmSelection, algorithms)
            }

            override fun <E> createCheckBoxList(selection: MutableSingleSelection<E>, checks: MutableSelection<E>, values: Iterable<E>): CPComponent {
                return CPCheckBoxList(selection, checks, values.toList())
            }

            override fun createVerticalPanel(): CPComponent {
                return CPVerticalPanel()
            }

            override fun createFormPanel(): CPFormPanel {
                return CPFormPanel()
            }

            override fun createGridPanel(rows: Int, columns: Int, vararg components: CPComponent): CPComponent {
                return CPGridPanel(rows, columns, *components)
            }

            override fun createSwitch(property: MutableProperty<Boolean>?): CPSwitch {
                return CPSwitch(property)
            }

            override fun createSwitch(): CPSwitch {
                return CPSwitch()
            }
        }
    }
}